import { type CaptureFlags } from '../ports/flags';
import { type CaptureHostInputs } from '../ports/host-inputs';
import { type CaptureLogger } from '../ports/logger';
import { type CapturePerfSink } from '../ports/perf';
import { type CapturePersistencePolicy } from '../ports/persistence-policy';
import { type CaptureUserContext } from '../ports/user-context';

// The package configuration core. Every seam is a late-binding delegate read at
// call time, never at configure time, so captures made during module evaluation
// keep following reconfiguration.
//
// Defaults are package-safe fallbacks, not app parity: they all fail closed.
// This module's import graph must stay engine-free (no stores, no Dexie, no
// recorders) — importing the configure door must never initialize the engine.
interface CaptureConfiguration {
  loggerBase: CaptureLogger;
  flags: CaptureFlags;
  userContext: CaptureUserContext;
  hostInputs: CaptureHostInputs;
  perf: CapturePerfSink;
  persistence: CapturePersistencePolicy;
}

/* eslint-disable no-console -- the documented package-safe default logger is
   the console; hosts replace it via configureCapture at bootstrap. */
const consoleLogger: CaptureLogger = {
  trace: (...args) => console.debug(...args),
  debug: (...args) => console.debug(...args),
  info: (...args) => console.info(...args),
  warn: (...args) => console.warn(...args),
  error: (...args) => console.error(...args),
  fatal: (...args) => console.error(...args),
  log: (...args) => console.log(...args),
  child: () => consoleLogger,
};
/* eslint-enable no-console */

const defaultConfiguration = (): CaptureConfiguration => ({
  loggerBase: consoleLogger,
  flags: {
    isDefaultTelehealthModeEnabled: () => false,
    isTelehealthAllowCurrentTabEnabled: () => false,
    useTranscriptionServiceV2: () => false,
    isMediaRecorderTimesliceEnabled: () => false,
  },
  userContext: {
    getCurrentUser: () => undefined,
    resolveCurrentUserRefId: () => Promise.resolve(undefined),
  },
  perf: {
    appendStartRecordingCheckpoint: () => undefined,
    cancelStartRecordingTracking: () => undefined,
    finishStartRecordingTrackingFailure: () => undefined,
  },
  hostInputs: {
    // Fail-safe: an unconfigured host never initializes the web audio-level
    // monitor or opens a webview mic stream.
    micReadiness: {
      getSnapshot: () => false,
      subscribe: () => () => undefined,
      setReady: () => undefined,
    },
    isNativeCaptureHost: () => false,
  },
  // Fails closed to legacy; coalescing and page-lifetime pinning belong to the
  // pipeline-mode coordinator, not here.
  persistence: {
    resolveLedgerEnabled: () =>
      Promise.resolve({ enabled: false, reason: 'unconfigured' }),
  },
});

const configuration: CaptureConfiguration = defaultConfiguration();

export interface CaptureConfigurationPatch {
  loggerBase?: CaptureLogger;
  flags?: Partial<CaptureFlags>;
  userContext?: Partial<CaptureUserContext>;
  hostInputs?: Partial<CaptureHostInputs>;
  perf?: Partial<CapturePerfSink>;
  persistence?: Partial<CapturePersistencePolicy>;
}

// Facades with subscription state (the mic-readiness facade) observe
// configuration changes through this hook so a source swap can detach old
// subscriptions and notify once — never through polling.
type ConfigurationChangeListener = () => void;
const changeListeners = new Set<ConfigurationChangeListener>();

export const onCaptureConfigurationChange = (
  listener: ConfigurationChangeListener
): (() => void) => {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
};

const notifyConfigurationChanged = (): void => {
  changeListeners.forEach((listener) => listener());
};

const assignDefinedMembers = <T extends object>(target: T, patch: Partial<T>): void => {
  (Object.keys(patch) as Array<keyof T>).forEach((key) => {
    const value = patch[key];
    if (value !== undefined) {
      target[key] = value;
    }
  });
};

// Field-wise patch: nested bags merge only their supplied members, last write
// wins per field, and repeat calls are idempotent. Atomic values (the logger
// base) replace whole.
export const configureCapture = (patch: CaptureConfigurationPatch): void => {
  if (patch.loggerBase !== undefined) {
    configuration.loggerBase = patch.loggerBase;
  }
  if (patch.flags !== undefined) {
    assignDefinedMembers(configuration.flags, patch.flags);
  }
  if (patch.userContext !== undefined) {
    assignDefinedMembers(configuration.userContext, patch.userContext);
  }
  if (patch.hostInputs !== undefined) {
    assignDefinedMembers(configuration.hostInputs, patch.hostInputs);
  }
  if (patch.perf !== undefined) {
    assignDefinedMembers(configuration.perf, patch.perf);
  }
  if (patch.persistence !== undefined) {
    assignDefinedMembers(configuration.persistence, patch.persistence);
  }
  notifyConfigurationChanged();
};

export const getConfiguredLoggerBase = (): CaptureLogger => configuration.loggerBase;

export const getConfiguredFlags = (): CaptureFlags => configuration.flags;

export const getConfiguredUserContext = (): CaptureUserContext =>
  configuration.userContext;

export const getConfiguredHostInputs = (): CaptureHostInputs => configuration.hostInputs;

export const getConfiguredPerfSink = (): CapturePerfSink => configuration.perf;

export const getConfiguredPersistencePolicy = (): CapturePersistencePolicy =>
  configuration.persistence;

// Test-only: exposed through the `./testing` door, never `./configure`, so
// suites can isolate configuration state between tests.
export const resetCaptureConfiguration = (): void => {
  Object.assign(configuration, defaultConfiguration());
  notifyConfigurationChanged();
};
